import React from 'react';
import type { ButtonProps } from 'semantic-ui-react';
import { Button as SemanticButton, Popup, Loader } from 'semantic-ui-react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

import { RawButtonLinkOrAnchor } from './RawButtonLinkOrAnchor';

export enum RawButtonPatterns {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  BRAND = 'brand',
}

export enum RawButtonVariations {
  FILLED = 'filled',
  OUTLINE = 'outline',
  MINIMAL = 'minimal',
}

export type RawButtonProps = {
  fluid?: boolean;
  children?: React.ReactNode;
  className?: string;
  /** Which end the label and icon sit at. Only bites on a `fluid` button, which has room spare. */
  alignContent?: 'left' | 'center' | 'right';
  disabled?: boolean;
  /**
   * Looks and announces disabled while staying focusable and still calling `onClick`. For a control
   * that has to explain its own refusal: a natively `disabled` one dispatches no click to explain.
   */
  softDisabled?: boolean;
  Icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  id?: string;
  inverse?: boolean;
  name?: string;
  onClick?: (
    event: React.MouseEvent<
      HTMLButtonElement | HTMLAnchorElement | HTMLElement | HTMLFormElement
    >
  ) => Promise<unknown> | void;
  // For gestures that are not a click. Semantic spreads whatever it does not handle itself.
  onDoubleClick?: React.MouseEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  onPointerDown?: React.PointerEventHandler<HTMLElement>;
  /*
    **A `Tooltip` anchor needs these.** Semantic's `Portal` makes a trigger interactive by cloning
    them onto it, and a prop this component does not forward is dropped - so a button handed
    straight to a tooltip rendered perfectly while the tooltip never opened.
  */
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  pattern?: RawButtonPatterns | `${RawButtonPatterns}`;
  tabIndex?: number;
  title?: string;
  /**
   * Saves what `to` points at instead of going there, under this name. Needs a `to`: it is the
   * anchor's own attribute, so there has to be an anchor and an href for it to act on.
   *
   * It also settles which anchor gets built - a plain one rather than the router's, which would
   * treat a same-origin path as a route and navigate instead of saving.
   */
  download?: string;
  to?: string | null;
  toTab?: string | null;
  type?: 'button' | 'submit';
  variation?: RawButtonVariations | `${RawButtonVariations}`;
};

export const RawButton = ({
  fluid = false,
  children = null,
  className = '',
  alignContent = 'center',
  disabled = false,
  softDisabled = false,
  Icon = null,
  iconPosition = 'right',
  id,
  inverse = false,
  name,
  onBlur,
  onClick,
  onDoubleClick,
  onFocus,
  onKeyDown,
  onMouseEnter,
  onMouseLeave,
  onPointerDown,
  pattern = RawButtonPatterns.PRIMARY,
  title,
  tabIndex,
  download,
  to,
  toTab,
  type = 'button',
  variation = RawButtonVariations.FILLED,
}: RawButtonProps) => {
  const [loading, setLoading] = React.useState(false);

  const waitForClick = async (
    event: React.MouseEvent<
      HTMLButtonElement | HTMLAnchorElement | HTMLElement | HTMLFormElement
    >
  ) => {
    const pending = onClick?.(event);

    /*
      **Only a handler with something to wait for gets the loader.** Awaiting a synchronous one
      still defers to a microtask, so the button committed a render wearing the spinner and swapped
      its glyph for a node of its own - which a second click then landed on, and no `dblclick`.
    */
    if (!pending) return;

    setLoading(true);
    try {
      await pending;
    } finally {
      setLoading(false);
    }
  };
  const attrs: ButtonProps = {
    className: AttrsHelper.formatClassname(
      className,
      fluid && 'fluid',
      inverse && 'inverse',
      `icon-position-${iconPosition}`,
      // Only when it is not the default, so every existing button's class list is left alone.
      alignContent !== 'center' && `align-content-${alignContent}`,
      // Semantic adds this itself for a natively disabled button; the explaining form needs it too.
      softDisabled && 'disabled',
      pattern,
      variation
    ),
    'aria-disabled': softDisabled || undefined,
    disabled: disabled || loading,
    'data-test': name,
    id,
    onClick: event => {
      /*
        Rethrowing from a timer hands the failure to the page's error handling; discarding the
        promise would end the click as an unhandled rejection instead.
      */
      waitForClick(event).catch((error: unknown) => {
        setTimeout(() => {
          throw error;
        });
      });
    },
    onBlur,
    onDoubleClick,
    onFocus,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    tabIndex,
    type,
  };

  const displayIcon = loading ? (
    <span className="rd-icon" data-test="loading-icon">
      <Loader inline size="tiny" active />
    </span>
  ) : (
    Icon
  );
  const linkTo = to || toTab;
  if (linkTo) {
    attrs.as = RawButtonLinkOrAnchor;
    attrs.linkTo = linkTo;
    attrs.toTab = toTab;
    attrs.download = download;
  }

  const Button = React.createElement(
    SemanticButton,
    attrs,
    <>
      {iconPosition === 'left' && displayIcon}
      {children}
      {iconPosition === 'right' && displayIcon}
    </>
  );
  /*
    One tooltip, and the styled one - a native `title` would stack the browser's on top of it.
    Named like `Tooltip`'s own surface so a test can reach a popup that is portalled away.
  */
  return title ? (
    <Popup on="hover" trigger={Button} closeOnTriggerClick>
      <span data-test={name ? `${name}-title` : undefined}>{title}</span>
    </Popup>
  ) : (
    Button
  );
};
